import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  CircularProgress,
  LinearProgress,
  Divider,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import { 
  Update as UpdateIcon,
  SportsEsports as GamesIcon,
  Devices as ConsolesIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axiosInstance from '../axiosInstance';
import { useSnackbar } from 'notistack';
import { getGameMarketValue, getConsoleMarketValue, updateGamePrice, updateConsolePrice } from '../utils/cexUtils';

/**
 * Component to bulk update game and console prices with CEX market values
 * @param {Object} props - Component props
 * @param {string} props.userId - User ID
 * @param {Function} props.onUpdateComplete - Callback when update is complete
 */
const BulkCexUpdate = ({ userId, onUpdateComplete }) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle, processing, complete, error
  const [stats, setStats] = useState({
    total: 0,
    updated: 0,
    failed: 0,
    skipped: 0,
    games: {
      total: 0,
      updated: 0,
      failed: 0
    },
    consoles: {
      total: 0,
      updated: 0,
      failed: 0
    }
  });
  const { enqueueSnackbar } = useSnackbar();

  const updateAllPrices = async () => {
    if (!userId || loading) return;
    
    setLoading(true);
    setStatus('processing');
    setProgress(0);
    setStats({
      total: 0,
      updated: 0,
      failed: 0,
      skipped: 0,
      games: {
        total: 0,
        updated: 0,
        failed: 0
      },
      consoles: {
        total: 0,
        updated: 0,
        failed: 0
      }
    });
    
    try {
      // Fetch all games and consoles for the user
      const [gamesResponse, consolesResponse] = await Promise.all([
        axiosInstance.get(`/games/user/${userId}`),
        axiosInstance.get(`/consoles/user/${userId}`)
      ]);
      
      const games = gamesResponse.data || [];
      const consoles = consolesResponse.data || [];
      
      const totalItems = games.length + consoles.length;
      setStats(prev => ({
        ...prev,
        total: totalItems,
        games: { ...prev.games, total: games.length },
        consoles: { ...prev.consoles, total: consoles.length }
      }));
      
      if (totalItems === 0) {
        enqueueSnackbar('No games or consoles found in your collection.', { variant: 'info' });
        setStatus('complete');
        setLoading(false);
        return;
      }
      
      // Process games
      let processedItems = 0;
      
      // Update games with CEX market values
      for (const game of games) {
        try {
          // Skip if the game doesn't have a name or platform
          if (!game.name || !game.platform) {
            setStats(prev => ({
              ...prev,
              skipped: prev.skipped + 1
            }));
            processedItems++;
            setProgress(Math.floor((processedItems / totalItems) * 100));
            continue;
          }
          
          // Get market value for the game
          const marketValue = await getGameMarketValue(game.name, game.platform);
          
          // Skip if no market value found
          if (!marketValue || !marketValue.found) {
            setStats(prev => ({
              ...prev,
              skipped: prev.skipped + 1
            }));
            processedItems++;
            setProgress(Math.floor((processedItems / totalItems) * 100));
            continue;
          }
          
          // Update the game price
          await updateGamePrice(game._id, marketValue);
          
          setStats(prev => ({
            ...prev,
            updated: prev.updated + 1,
            games: {
              ...prev.games,
              updated: prev.games.updated + 1
            }
          }));
        } catch (err) {
          console.error(`Error updating game ${game.name}:`, err);
          setStats(prev => ({
            ...prev,
            failed: prev.failed + 1,
            games: {
              ...prev.games,
              failed: prev.games.failed + 1
            }
          }));
        } finally {
          processedItems++;
          setProgress(Math.floor((processedItems / totalItems) * 100));
        }
      }
      
      // Update consoles with CEX market values
      for (const console of consoles) {
        try {
          // Skip if the console doesn't have a name
          if (!console.name) {
            setStats(prev => ({
              ...prev,
              skipped: prev.skipped + 1
            }));
            processedItems++;
            setProgress(Math.floor((processedItems / totalItems) * 100));
            continue;
          }
          
          // Get market value for the console
          const marketValue = await getConsoleMarketValue(console.name);
          
          // Skip if no market value found
          if (!marketValue || !marketValue.found) {
            setStats(prev => ({
              ...prev,
              skipped: prev.skipped + 1
            }));
            processedItems++;
            setProgress(Math.floor((processedItems / totalItems) * 100));
            continue;
          }
          
          // Update the console price
          await updateConsolePrice(console._id, marketValue);
          
          setStats(prev => ({
            ...prev,
            updated: prev.updated + 1,
            consoles: {
              ...prev.consoles,
              updated: prev.consoles.updated + 1
            }
          }));
        } catch (err) {
          console.error(`Error updating console ${console.name}:`, err);
          setStats(prev => ({
            ...prev,
            failed: prev.failed + 1,
            consoles: {
              ...prev.consoles,
              failed: prev.consoles.failed + 1
            }
          }));
        } finally {
          processedItems++;
          setProgress(Math.floor((processedItems / totalItems) * 100));
        }
      }
      
      enqueueSnackbar(`Updated ${stats.updated} items with CEX market values`, { variant: 'success' });
      setStatus('complete');
      
      // Call the onUpdateComplete callback if provided
      if (onUpdateComplete) {
        onUpdateComplete(stats);
      }
    } catch (err) {
      console.error('Error updating prices:', err);
      enqueueSnackbar(`Failed to update prices: ${err.message}`, { variant: 'error' });
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          <UpdateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Bulk Update CEX Market Values
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UpdateIcon />}
          onClick={updateAllPrices}
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update All Prices'}
        </Button>
      </Box>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        This will update the price field of all your games and consoles with the current CEX market value.
        The process may take a few minutes depending on the size of your collection.
      </Typography>
      
      {status === 'processing' && (
        <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            {progress}% Complete
          </Typography>
        </Box>
      )}
      
      {status === 'complete' && (
        <>
          <Divider sx={{ my: 2 }} />
          
          <Alert severity="success" sx={{ mb: 2 }}>
            Update complete! {stats.updated} items updated with CEX market values.
          </Alert>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Stack direction="row" spacing={1}>
              <Chip 
                icon={<GamesIcon />} 
                label={`Games: ${stats.games.updated}/${stats.games.total} updated`} 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                icon={<ConsolesIcon />} 
                label={`Consoles: ${stats.consoles.updated}/${stats.consoles.total} updated`} 
                color="secondary" 
                variant="outlined" 
              />
            </Stack>
            
            <Stack direction="row" spacing={1}>
              <Chip 
                icon={<CheckIcon />} 
                label={`${stats.updated} updated`} 
                color="success" 
                variant="outlined" 
              />
              <Chip 
                icon={<WarningIcon />} 
                label={`${stats.failed} failed`} 
                color="error" 
                variant="outlined" 
              />
              <Chip 
                label={`${stats.skipped} skipped`} 
                color="default" 
                variant="outlined" 
              />
            </Stack>
          </Box>
        </>
      )}
      
      {status === 'error' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          An error occurred while updating prices. Please try again later.
        </Alert>
      )}
    </Paper>
  );
};

export default BulkCexUpdate;
